<template>
    <header class="site-header" :class="{ 'header--scrolled': scrolled, 'header--menu-open': menuOpen }">
      <div class="header-inner">
  
        <!-- Logo -->
        <router-link to="/home" class="header-logo" aria-label="Home">
          <img
            v-if="details.logo"
            :src="url.hostName + 'images/logo/' + details.logo"
            alt="Logo"
            class="logo-img"
          />
          <img v-else src="/static/img/logo2.png" alt="Logo" class="logo-img" />
        </router-link>
  
        <!-- Desktop Nav -->
        <nav class="desktop-nav" aria-label="Main navigation">
          <router-link class="nav-link" to="/home">Home</router-link>
          <router-link class="nav-link" to="/about">About</router-link>
          <router-link class="nav-link" to="/faq">FAQ</router-link>
          <router-link class="nav-link" to="/pricing">Pricing</router-link>
          <router-link class="nav-link" to="/blog">Blog</router-link>
          <router-link class="nav-link" to="/contact">Contact</router-link>
        </nav>
  
        <!-- Desktop CTA -->
        <div class="header-cta">
          <router-link to="/pricing" class="btn-quote-sm">Request a Quote</router-link>
          <template v-if="isLogin">
            <router-link to="/" class="btn-ghost-sm">Dashboard</router-link>
            <button class="btn-primary-sm" :disabled="logoutLoading" @click="logout">
              <font-awesome-icon v-if="logoutLoading" icon="spinner" spin />
              <span>{{ logoutLoading ? 'Signing out...' : 'Sign Out' }}</span>
            </button>
          </template>
          <template v-else>
            <router-link to="/auth/login" class="btn-ghost-sm">Login</router-link>
            <router-link to="/auth/register" class="btn-primary-sm">Get Started</router-link>
          </template>
        </div>
  
        <!-- Mobile hamburger -->
        <button
          class="hamburger"
          :class="{ open: menuOpen }"
          @click="menuOpen = !menuOpen"
          aria-label="Toggle menu"
          :aria-expanded="menuOpen"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
  
      </div>
  
      <!-- Mobile drawer -->
      <div class="mobile-drawer" :class="{ 'drawer--open': menuOpen }">
        <nav class="mobile-nav" aria-label="Mobile navigation">
          <router-link class="mobile-nav-link" to="/home"    @click="menuOpen = false">Home</router-link>
          <router-link class="mobile-nav-link" to="/about"   @click="menuOpen = false">About</router-link>
          <router-link class="mobile-nav-link" to="/faq"     @click="menuOpen = false">FAQ</router-link>
          <router-link class="mobile-nav-link" to="/pricing" @click="menuOpen = false">Pricing</router-link>
          <router-link class="mobile-nav-link" to="/blog"    @click="menuOpen = false">Blog</router-link>
          <router-link class="mobile-nav-link" to="/contact" @click="menuOpen = false">Contact</router-link>
        </nav>
  
        <div class="mobile-cta">
          <router-link to="/pricing" class="btn-quote-sm w-full" @click="menuOpen = false">Request a Quote</router-link>
          <template v-if="isLogin">
            <router-link to="/" class="btn-ghost-sm w-full" @click="menuOpen = false">Dashboard</router-link>
            <button class="btn-primary-sm w-full" :disabled="logoutLoading" @click="logout">
              {{ logoutLoading ? 'Signing out...' : 'Sign Out' }}
            </button>
          </template>
          <template v-else>
            <router-link to="/auth/login"    class="btn-ghost-sm w-full" @click="menuOpen = false">Login</router-link>
            <router-link to="/auth/register" class="btn-primary-sm w-full" @click="menuOpen = false">Get Started</router-link>
          </template>
        </div>
      </div>
  
      <!-- Overlay -->
      <div class="mobile-overlay" :class="{ open: menuOpen }" @click="menuOpen = false"></div>
    </header>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import hostName from '../main';
  
  export default {
    name: 'PublicHeader',
  
    data() {
      return {
        isLogin:       !!localStorage.getItem('token'),
        logoutLoading: false,
        menuOpen:      false,
        scrolled:      false,
        details:       {},
        toast:         useToast(),
        url:           hostName,
      };
    },
  
    methods: {
      handleScroll() {
        this.scrolled = window.scrollY > 20;
      },
  
      logout() {
        this.logoutLoading = true;
        axios.get('/logout')
          .then(res => {
            if (res.status === 200) {
              localStorage.clear();
              window.location.href = '/auth/login';
            }
          })
          .catch(err => {
            this.logoutLoading = false;
            this.toast.error(err?.response?.data?.message ?? 'Logout failed.');
          });
      },
  
      pageData() {
        axios.get('/admin/fetchProjectSetup')
          .then(res => { this.details = res.data.data; })
          .catch(() => {});
      },
  
      async getCountry() {
        if (localStorage.getItem('country_code')) return;
        try {
          const { data } = await axios.get('/get-visitor-country');
          if (data.data?.countryCode) {
            localStorage.setItem('country',      data.data.country);
            localStorage.setItem('country_code', data.data.countryCode);
          } else {
            throw new Error('no countryCode');
          }
        } catch (_) {
          localStorage.setItem('country',      'United Kingdom');
          localStorage.setItem('country_code', 'GB');
        }
      },
    },
  
    mounted() {
      this.pageData();
      this.getCountry();
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    },
  
    beforeUnmount() {
      window.removeEventListener('scroll', this.handleScroll);
    },
  };
  </script>
  
  <style scoped>
  /* ── Header shell ────────────────────────────────────────────────────── */
  .site-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.25s ease, background 0.25s ease;
  }
  
  .site-header.header--scrolled {
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  }
  
  /* ── Inner layout ────────────────────────────────────────────────────── */
  .header-inner {
    display: flex;
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    height: 68px;
    gap: 32px;
  }
  
  /* ── Logo ────────────────────────────────────────────────────────────── */
  .header-logo { display: flex; align-items: center; flex-shrink: 0; text-decoration: none; }
  .logo-img    { max-height: 44px; width: auto; object-fit: contain; }
  
  /* ── Desktop nav ─────────────────────────────────────────────────────── */
  .desktop-nav {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    justify-content: center;
  }
  
  .nav-link {
    position: relative;
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    text-decoration: none;
    border-radius: 7px;
    transition: color 0.15s, background 0.15s;
    white-space: nowrap;
  }
  
  .nav-link:hover {
    color: #004AAD;
    background: rgba(0, 74, 173, 0.06);
  }
  
  .nav-link.router-link-active {
    color: #004AAD;
    font-weight: 600;
  }
  
  .nav-link.router-link-active::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 12px;
    right: 12px;
    height: 2px;
    background: #004AAD;
    border-radius: 2px;
  }
  
  /* ── CTA buttons ─────────────────────────────────────────────────────── */
  .header-cta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  
  .btn-quote-sm {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    border: 1.5px solid #004AAD;
    border-radius: 8px;
    background: transparent;
    color: #004AAD;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }
  .btn-quote-sm:hover { background: #004AAD; color: #fff; text-decoration: none; }

  .btn-ghost-sm {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    border: 1px solid #dde5f0;
    border-radius: 8px;
    background: transparent;
    color: #1a2b45;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }
  .btn-ghost-sm:hover { background: #f4f7fb; color: #004AAD; border-color: #c5d6f5; text-decoration: none; }
  
  .btn-primary-sm {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border: none;
    border-radius: 8px;
    background: #004AAD;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s, transform 0.1s;
    white-space: nowrap;
  }
  .btn-primary-sm:hover:not(:disabled)  { background: #003b8e; color: #fff; text-decoration: none; }
  .btn-primary-sm:active:not(:disabled) { transform: scale(0.97); }
  .btn-primary-sm:disabled { opacity: 0.55; cursor: not-allowed; }
  
  .w-full { width: 100%; justify-content: center; }
  
  /* ── Hamburger ───────────────────────────────────────────────────────── */
  .hamburger {
    display: none;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 36px;
    height: 36px;
    padding: 4px;
    background: transparent;
    border: 1px solid #dde5f0;
    border-radius: 8px;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s;
  }
  .hamburger:hover { background: #f4f7fb; }
  
  .hamburger span {
    display: block;
    height: 2px;
    border-radius: 2px;
    background: #1a2b45;
    transition: transform 0.25s ease, opacity 0.2s ease, width 0.2s ease;
    transform-origin: center;
  }
  
  .hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
  .hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
  .hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
  
  /* ── Mobile drawer ───────────────────────────────────────────────────── */
  .mobile-drawer {
    display: none;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border-top: 1px solid #f0f4f8;
    box-shadow: 0 16px 40px rgba(0,0,0,0.12);
    padding: 16px 24px 24px;
    gap: 4px;
    transform: translateY(-8px);
    opacity: 0;
    pointer-events: none;
    transition: transform 0.25s ease, opacity 0.2s ease;
  }
  
  .mobile-drawer.drawer--open {
    transform: translateY(0);
    opacity: 1;
    pointer-events: all;
  }
  
  .mobile-nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 16px;
  }
  
  .mobile-nav-link {
    display: block;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    text-decoration: none;
    transition: background 0.12s, color 0.12s;
  }
  .mobile-nav-link:hover,
  .mobile-nav-link.router-link-active {
    background: rgba(0, 74, 173, 0.06);
    color: #004AAD;
  }
  
  .mobile-cta {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 14px;
    border-top: 1px solid #f0f4f8;
  }
  
  /* ── Overlay ─────────────────────────────────────────────────────────── */
  .mobile-overlay {
    display: none;
    position: fixed;
    inset: 0;
    top: 68px;
    background: rgba(0,0,0,0.3);
    z-index: 999;
    cursor: pointer;
  }
  .mobile-overlay.open { display: block; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .desktop-nav { display: none; }
    .header-cta  { display: none; }
    .hamburger   { display: flex; }
    .mobile-drawer { display: flex; z-index: 1000; }
  }
  
  @media (max-width: 480px) {
    .header-inner { padding: 0 16px; gap: 16px; }
  }
  </style>